<script setup lang="ts">
import type { DistributionGroupWithID, EmissionType } from '#design-tokenomics/types'
import type { ColumnDef } from '@forgd/ui-kit/blocks/data-table'
import { useNumberFieldBlurGuard } from '#design-tokenomics/composables/useNumberFieldBlurGuard'
import { ColumnHeader, DataTable, InputCell } from '@forgd/ui-kit/blocks/data-table'
import { NoData } from '@forgd/ui-kit/blocks/empty-states'
import { Button } from '@forgd/ui-kit/button'
import { InputGroup, InputGroupAddon } from '@forgd/ui-kit/input-group'
import { NumberField, NumberFieldInput } from '@forgd/ui-kit/number-field'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@forgd/ui-kit/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@forgd/ui-kit/tooltip'
import { ChevronUpDownIcon } from '@heroicons/vue/24/outline'

defineProps<{
  groups: Array<DistributionGroupWithID>
}>()

const emit = defineEmits<{
  moveGroup: [id: string, type: EmissionType]
}>()

const unlockFrequencyOptions = [
  {
    label: 'Daily',
    value: 'daily',
  },
  {
    label: 'Monthly',
    value: 'monthly',
  },
] as const

const percentFormat = { maximumFractionDigits: 2 }
const percentGuard = useNumberFieldBlurGuard(percentFormat)
const monthsFormat = { maximumFractionDigits: 0 }
const monthsGuard = useNumberFieldBlurGuard(monthsFormat)

interface NumberInputOptions {
  value: number | null | undefined
  label: string
  suffix: '%' | 'Months'
  onUpdate: (value: number | null) => void
}

function numberInput({ value, label, suffix, onUpdate }: NumberInputOptions) {
  const guard = suffix === '%' ? percentGuard : monthsGuard
  return h(NumberField, {
    'modelValue': value ?? undefined,
    'onUpdate:modelValue': (v: number | undefined) => guard.accepts(v, value) && onUpdate(v ?? null),
    'min': 0,
    'formatOptions': suffix === '%' ? percentFormat : monthsFormat,
    'stepSnapping': false,
  }, () => h(InputGroup, { size: 'sm' }, () => [
    h(NumberFieldInput, { ...guard.inputListeners, 'aria-label': label }),
    h(InputGroupAddon, null, () => suffix),
  ]))
}

const columns: ColumnDef<DistributionGroupWithID>[] = [
  {
    accessorKey: 'name',
    size: 200,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Group Name/Allocation %', class: 'pr-1 pl-2', tooltip: 'The groups and their allocations have been defined in Token Distribution. To change them, head back there.' }),
    cell: ({ row, column }) => h(InputCell, { column, class: 'flex items-center gap-2' }, () => [
      h('div', { class: 'h-10 w-1 rounded-full', style: `background-color: ${row.original.color}` }),
      h('div', {}, [
        h('div', {}, row.original.name),
        h('div', { class: 'text-xs font-semibold' }, `${row.original.allocationPercentage}%`),
      ]),
    ]),
  },
  {
    accessorKey: 'unlockAtTgePercentage',
    size: 155,
    maxSize: 155,
    header: ({ column }) => h(ColumnHeader, { column, label: '% Unlock at TGE', class: 'px-1', tooltip: 'The share of this group\'s allocation that unlocks immediately at TGE.' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => numberInput({
      value: row.original.unlockAtTgePercentage,
      label: '% Unlock at TGE',
      suffix: '%',
      onUpdate: (v) => {
        row.original.unlockAtTgePercentage = v ?? 0
      },
    })),
  },
  {
    accessorKey: 'lockupDurationInMonths',
    size: 155,
    maxSize: 155,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Lock Up Duration', class: 'px-1' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => numberInput({
      value: row.original.lockupDurationInMonths,
      label: 'Lock Up Duration',
      suffix: 'Months',
      onUpdate: (v) => {
        row.original.lockupDurationInMonths = v
      },
    })),
  },
  {
    accessorKey: 'unlockAtInitialCliffPercentage',
    size: 155,
    maxSize: 155,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Unlock at Initial Cliff', class: 'px-1', tooltip: 'The share of this group\'s allocation that unlocks once the lock up period ends, before the remaining tokens start vesting.' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => numberInput({
      value: row.original.unlockAtInitialCliffPercentage,
      label: 'Unlock at Initial Cliff',
      suffix: '%',
      onUpdate: (v) => {
        row.original.unlockAtInitialCliffPercentage = v
      },
    })),
  },
  {
    accessorKey: 'unlockDurationInMonths',
    size: 155,
    maxSize: 155,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Unlock Duration', class: 'px-1' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => numberInput({
      value: row.original.unlockDurationInMonths,
      label: 'Unlock Duration',
      suffix: 'Months',
      onUpdate: (v) => {
        row.original.unlockDurationInMonths = v
      },
    })),
  },
  {
    accessorKey: 'unlockFrequency',
    size: 155,
    maxSize: 155,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Unlock Frequency', class: 'px-1' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => h(Select<typeof unlockFrequencyOptions[number]['value'] | null>, {
      'modelValue': row.original.unlockFrequency,
      'onUpdate:modelValue': (value) => {
        row.original.unlockFrequency = value
      },
    }, () => [
      h(SelectTrigger, { size: 'sm' }, () => h(SelectValue)),
      h(SelectContent, () => unlockFrequencyOptions.map(o =>
        h(SelectItem, { value: o.value }, () => o.label),
      )),
    ])),
  },
  {
    id: 'move',
    size: 44,
    maxSize: 44,
    header: '',
    cell: ({ row, column }) => h(InputCell, { column }, () => h(Tooltip, () => [
      h(TooltipTrigger, { asChild: true }, () => h(Button, {
        'variant': 'outline',
        'size': 'sm',
        'iconOnly': true,
        'aria-label': 'Move this group to another emission schedule',
        'onClick': () => emit('moveGroup', row.original.id, 'lock-and-vest'),
      }, () => h(ChevronUpDownIcon, { 'aria-hidden': 'true' }))),
      h(TooltipContent, () => 'Move this group to another emission schedule'),
    ])),
  },
]
</script>

<template>
  <div data-testid="DtEmissionTableLockVest">
    <DataTable :columns="columns" :data="groups" size="md">
      <template #empty>
        <NoData message="No groups assigned to this emission type" />
      </template>
    </DataTable>
  </div>
</template>
